import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, map, Observable, switchMap, take } from 'rxjs';
import { FilterCriteria } from '@wdx/kamino/state';
import {
    selectDisableForFeature,
    selectLocalOnlyForFeature,
    selectPagination,
    selectSearchForFeature,
    selectSelectedFieldsForFeature,
    setDisable,
    setLocalOnly,
    setSearch,
    setSelectedFields,
} from '@wdx/kamino/state';
import { ActiveFeatureFacadeService } from '../active-feature/active-feature-facade.service';

@Injectable({ providedIn: 'root' })
export class FiltersService {
    private store$ = inject(Store);
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private activeFeatureFacadeService = inject(ActiveFeatureFacadeService);

    private filters$ = this.activeFeatureFacadeService.activeFeatureCode$.pipe(
        switchMap((featureCode) => this.getFiltersForFeature(featureCode)),
    );

    public isDisable$ = this.filters$.pipe(map((f) => f.isDisable));
    public isLocal$ = this.filters$.pipe(map((f) => f.isLocal));
    public search$ = this.filters$.pipe(map((f) => f.search));
    public selectedFields$ = this.filters$.pipe(map((f) => f.selectedFields));
    public pagination$ = this.store$.select(selectPagination);

    public getFiltersForFeature(
        featureCode: string,
    ): Observable<FilterCriteria> {
        return combineLatest([
            this.store$.select(selectDisableForFeature(featureCode)),
            this.store$.select(selectLocalOnlyForFeature(featureCode)),
            this.store$.select(selectSearchForFeature(featureCode)),
            this.store$.select(selectSelectedFieldsForFeature(featureCode)),
        ]).pipe(
            map(([isDisable, isLocal, search, selectedFields]) => ({
                isDisable,
                isLocal,
                search,
                selectedFields,
            })),
        );
    }

    public dispatchFilterByKey(
        featureCode: string,
        key: string,
        paramValue: string,
    ): void {
        switch (key) {
            case 'search':
                this.store$.dispatch(
                    setSearch({ featureCode, search: paramValue }),
                );
                break;
            case 'isDisable':
                this.store$.dispatch(
                    setDisable({
                        featureCode,
                        isDisable: paramValue === 'true',
                    }),
                );
                break;
            case 'isLocal':
                this.store$.dispatch(
                    setLocalOnly({
                        featureCode,
                        isLocal: paramValue === 'true',
                    }),
                );
                break;
        }
    }

    public onSearchChange(featureCode: string, value: string): void {
        this.store$.dispatch(setSearch({ featureCode, search: value }));
        this.updateQueryParams({ search: value || null });
    }

    public onLocalOnlyChange(featureCode: string, checked: boolean): void {
        this.store$.dispatch(setLocalOnly({ featureCode, isLocal: checked }));
        this.updateQueryParams({
            isLocal: checked ? 'true' : null,
        });
    }

    public onDisableChange(featureCode: string, checked: boolean): void {
        this.store$.dispatch(setDisable({ featureCode, isDisable: checked }));
        this.updateQueryParams({
            isDisable: checked ? 'true' : null,
        });
    }

    public onDynamicFilterSelect(
        featureCode: string,
        fieldCode: string,
        optionCode: string,
    ): void {
        this.store$
            .select(selectSelectedFieldsForFeature(featureCode))
            .pipe(take(1))
            .subscribe((currentSelection) => {
                const isCurrentlySelected =
                    currentSelection?.[fieldCode] === optionCode;
                const newSelection = isCurrentlySelected
                    ? {}
                    : { [fieldCode]: optionCode };

                this.store$.dispatch(
                    setSelectedFields({
                        featureCode,
                        selectedFields: newSelection,
                    }),
                );

                this.updateQueryParams({
                    [fieldCode]: isCurrentlySelected ? null : optionCode,
                });
            });
    }

    public applyQueryParamsToFilters(featureCode: string): void {
        const predefinedFilterKeys = ['isDisable', 'isLocal', 'search'];

        this.route.queryParamMap.pipe(take(1)).subscribe((queryParams) => {
            const selectedFields: Record<string, string> = {};

            queryParams.keys.forEach((key) => {
                const paramValue = queryParams.get(key);
                if (paramValue === null) {
                    return;
                }

                predefinedFilterKeys.includes(key)
                    ? this.dispatchFilterByKey(featureCode, key, paramValue)
                    : (selectedFields[key] = paramValue);
            });

            if (Object.keys(selectedFields).length > 0) {
                this.store$.dispatch(
                    setSelectedFields({ featureCode, selectedFields }),
                );
            }

            this.filters$
                .pipe(take(1))
                .subscribe(({ isDisable, isLocal, search, selectedFields }) => {
                    const urlParamsToAdd: Record<string, string> = {};

                    if (!queryParams.has('isDisable') && isDisable) {
                        urlParamsToAdd['isDisable'] = 'true';
                    }
                    if (!queryParams.has('isLocal') && isLocal) {
                        urlParamsToAdd['isLocal'] = 'true';
                    }
                    if (!queryParams.has('search') && search) {
                        urlParamsToAdd['search'] = search;
                    }

                    Object.entries(selectedFields ?? {}).forEach(
                        ([key, val]) => {
                            if (!queryParams.has(key)) {
                                urlParamsToAdd[key] = val;
                            }
                        },
                    );

                    if (Object.keys(urlParamsToAdd).length > 0) {
                        this.router.navigate([], {
                            relativeTo: this.route,
                            queryParams: urlParamsToAdd,
                            queryParamsHandling: 'merge',
                        });
                    }
                });
        });
    }

    updateQueryParams(params: Record<string, string | null>): void {
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: params,
            queryParamsHandling: 'merge',
            replaceUrl: false,
        });
    }
}
